import { useCallback, useMemo, useState } from 'react';

import { SelectChangeEvent } from '@mui/material';

import { FarmsParams, useFarms } from '@services/api/vaults/farms.api';

import useDebounce from '@hooks/common/useDebounce';
import useDexFactories from '@hooks/vaults/useDexFactories';

import { toggleFilterState } from '@helpers/vaults/vault.helpers';

import {
  AVAILABLE_CHAINS_LIST,
  CHAIN_ID,
  CHAIN_INFO,
} from '@constants/common/chain.constants';
import {
  DEFAULT_DELAY,
  SortDestination,
} from '@constants/common/common.constants';
import { ALL_FARM_TYPES, FarmSortBy } from '@constants/vaults/common.constants';

import { FarmDto } from '@dto/vaults/vault-factory.dto';

export interface FarmsFiltersActions {
  onSearch: (value: string) => void;
  onToggleFarmTypes: (value: string | number) => void;
  onSortBy: (value: string) => void;
  onClearFilters: () => void;
  onSortDirection: (direction: SortDestination) => void;
  onSetPlatform: (event: SelectChangeEvent<unknown>) => void;
}

export interface FarmsFiltersType {
  search: string;
  selectedChains: CHAIN_ID[];
  sortBy: FarmSortBy;
  farmTypes: string[];
  platforms: number[];
  debounceSearchValue: string;
  sortDirection: SortDestination;
  platformOptions: { name: string; value: number; subLabel: string }[];
  counterFilters: number;
}

interface UseFarmsList {
  farms: FarmDto[];
  totalCount: number;
  isPending: boolean;
  isNoResult: boolean;
  filters: FarmsFiltersType;
  actions: FarmsFiltersActions;
}

type Params = {
  selectedChains: number[];
};

const useFarmsList = ({ selectedChains }: Params): UseFarmsList => {
  const { dexFactories } = useDexFactories(selectedChains);

  const [search, setSearch] = useState<string>('');
  const [sortBy, setSortBy] = useState<FarmSortBy>(FarmSortBy.APR);
  const [farmTypes, setFarmTypes] = useState<string[]>([]);
  const [platforms, setPlatforms] = useState<number[]>([]);
  const [sortDirection, setSortDirection] = useState<SortDestination>(
    SortDestination.Desc,
  );

  const debounceSearchValue = useDebounce(search, { delay: DEFAULT_DELAY });

  const params: FarmsParams = useMemo(
    () => ({
      sortBy,
      sortDirection,
      vaultFactoryIds: [], // TODO: what is?
      dexFactoryIds: platforms,
      types: farmTypes,
      search: debounceSearchValue || undefined,
      chainIds: selectedChains,
    }),
    [
      debounceSearchValue,
      farmTypes,
      platforms,
      selectedChains,
      sortBy,
      sortDirection,
    ],
  );

  const { data, isError, isPending } = useFarms(params);

  const counterFilters = useMemo(() => {
    const chainsSelected =
      selectedChains.length < AVAILABLE_CHAINS_LIST.length &&
      !!selectedChains.length
        ? 1
        : 0;

    return (
      [
        !!search.length,
        !!farmTypes.length,
        !!platforms.length,
        sortBy !== FarmSortBy.APR,
      ].filter((item) => item).length + chainsSelected
    );
  }, [search, farmTypes, platforms, sortBy, selectedChains]);

  const farms = useMemo(() => {
    if (data && !isError) {
      return data.items;
    }
    return [];
  }, [data, isError]);

  const totalCount = useMemo(() => {
    if (data && !isError) {
      return data.count;
    }
    return 0;
  }, [data, isError]);

  const isNoResult = useMemo(
    () => !data?.items?.length && !isError && !isPending,
    [data, isError, isPending],
  );

  const platformOptions = useMemo(
    () =>
      dexFactories.length
        ? dexFactories.map((item) => ({
            name: item.name,
            value: item.id,
            subLabel: CHAIN_INFO[item.chainId as CHAIN_ID].name,
          }))
        : [],
    [dexFactories],
  );

  const onToggleFarmTypes = useCallback((value: string | number) => {
    setFarmTypes((prevSelected) =>
      toggleFilterState(prevSelected, String(value), ALL_FARM_TYPES),
    );
  }, []);

  const onSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const onSortBy = useCallback((newSortBy: string) => {
    setSortBy(newSortBy as FarmSortBy);
  }, []);

  const onSortDirection = useCallback((direction: SortDestination) => {
    setSortDirection(direction);
  }, []);

  const onSetPlatform = useCallback((event: SelectChangeEvent<unknown>) => {
    setPlatforms(event.target.value as number[]);
  }, []);

  const onClearFilters = useCallback(() => {
    setSortBy(FarmSortBy.APR);
    setSortDirection(SortDestination.Desc);
    setSearch('');
    setFarmTypes([]);
    setPlatforms([]);
  }, []);

  return {
    farms,
    totalCount,
    isPending,
    isNoResult,
    filters: {
      search,
      sortBy,
      farmTypes,
      platforms,
      debounceSearchValue,
      sortDirection,
      platformOptions,
      selectedChains,
      counterFilters,
    },
    actions: {
      onToggleFarmTypes,
      onSearch,
      onSortBy,
      onSortDirection,
      onSetPlatform,
      onClearFilters,
    },
  };
};

export default useFarmsList;
